import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { styled } from "@mui/styles";
import {
  colors,
  fontSize,
  fontWeight,
  gaps,
  padding,
  borderRadius,
} from "theme/defaultStyle";
import ActionButton from "controls/global/action-button/ActionButton";
import { OptionRenderType } from "controls/global/stewart-checkbox/StewartAutocompleteWithCheckboxes";
import ColumnsConfig, {
  MenuColumnItem,
} from "controls/global/columns-config/ColumnsConfig";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";
import { PolicyManagementRecord } from "entities/UIModel/PolicyManagementRecord";
import { useColumnSettings } from "utils/custom-hooks/useColumnSettings";
import { useCountyList } from "utils/custom-hooks/useCountyList";
import { usePolicyTypeOptions } from "utils/custom-hooks/usePolicyTypeOptions";
import { useFormPrefixOptions } from "utils/custom-hooks/useFormPrefixOptions";
import { useRecordTypeOptions } from "utils/custom-hooks/useRecordTypeOptions";
import { usePropertyTypeOptions } from "utils/custom-hooks/usePropertyTypeOptions";
import {
  useAsyncTextFilter,
  useAsyncOptionFilter,
} from "utils/custom-hooks/useAsyncTextFilter";
import { useTransCodeOptions } from "utils/custom-hooks/useTransCodeOptions";
import { useStateList } from "utils/custom-hooks/useStateList";
import { useDateTypeOptions } from "utils/custom-hooks/useDateTypeOptions";
import {
  getPolicyHistoryFormNameListByText,
  getPolicyHistoryFileNumberListByText,
  getPolicyHistoryOriginalPolicyNumberListByText,
  getPolicyHistoryClosedByListByText,
} from "utils/api/lookupApi";
import PolicyStatsBar from "../shared/PolicyStatsBar";
import BulkActionBar, { BulkButton } from "../shared/BulkActionBar";
import AdvancedSearchButton from "../../utils/AdvancedSearchButton";
import AdvancedSearchPanel, { parseIsoDate } from "../../utils/AdvancedSearchPanel";
import ExportButton from "../shared/ExportButton";
import HistoryTable from "./HistoryTable";
import { buildHistoryColumns, HISTORY_GRID_NAME } from "./historyColumns";
import { PolicyManagementCounts } from "../counts/types";
import FileExportDialog from "controls/global/file-export-dialog";
import IosShareIcon from '@mui/icons-material/IosShare';
import { orderBy as _orderBy } from "lodash";
import { exportPolicyHistory, PolicyTransactionCriteriaDto, PolicyTransactionExportCriteriaDto } from "utils/api/policyTransactionApi";
import { StageType } from "utils/data/enum";
import { BulkAdjustModal } from "../modals";
import { showToast } from "controls/global/stewart-toast";


const TabContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: gaps.medium1,
});

const ChipRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: gaps.small1,
  flexWrap: "wrap",
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ChipsSeparator = styled(Box)({
  width: 1,
  height: 22,
  backgroundColor: colors.grey09,
  marginLeft: gaps.xsmall2,
  marginRight: gaps.xsmall2,
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Chip = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: gaps.xsmall1,
  padding: `${padding.xsmall1} ${padding.medium1}`,
  borderRadius: borderRadius.xlarge1,
  fontSize: fontSize.small,
  fontWeight: fontWeight.normal2,
  cursor: "pointer",
  userSelect: "none",
  border: `1px solid ${colors.grey09}`,
  backgroundColor: colors.white,
  color: colors.grey11,
  whiteSpace: "nowrap",
  "&.active": {
    backgroundColor: colors.blue08,
    color: colors.blue02,
    borderColor: colors.blue01,
  },
});

const ToolbarSpacer = styled(Box)({
  marginLeft: "auto",
  display: "flex",
  gap: gaps.small1,
  alignItems: "center",
});

const SelectedCount = styled(Typography)({
  fontSize: fontSize.small,
  fontWeight: fontWeight.normal2,
  color: colors.blue15,
  whiteSpace: "nowrap",
  marginLeft: gaps.small1,
});

type HistoryChip =
  | "all"
  | "reg"
  | "si"
  | "adj"
  | "closed"
  | "sent"
  | "edited"
  | "unedited";

interface HistoryTabProps {
  records: PolicyManagementRecord[];
  allRecords: PolicyManagementRecord[];
  onUpdateRecords: (updatedRecords: PolicyManagementRecord[]) => void;
  counts: PolicyManagementCounts;
  stateVals?: string[];
  rowsPerPage?: number;
  onRowsPerPageChange?: (count: number) => void;
  page?: number;
  onPageChange?: (page: number) => void;
  totalRows?: number;
  totalPages?: number;
  isLoading?: boolean;
  sortColumn?: string;
  onSortChange?: (sortColumn: string) => void;
  filters?: Record<string, string | string[] | number | null>;
  onFilterChange?: (name: string, value: string | string[] | number | null) => void;
  onResetFilters?: () => void;
  onChipCriteriaChange?: (criteria: Record<string, unknown>) => void;
  onAdvancedCriteriaChange?: (criteria: Record<string, unknown>) => void;
  applyEnabled?: boolean;
  onApplyFilters?: () => void;
  onRefresh?: () => void;
  refetchCounts?: () => void;
  userTimeZone: string;
  // Global Search seed values to prefill matching column search boxes.
  headerFilterDefaults?: Record<string, string>;
}

function deriveHistoryChipCriteria(
  chips: Set<HistoryChip>
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (chips.has("adj")) out.RecordTypeCode = "A";
  if (chips.has("si")) out.IsSimultaneous = 1;
  if (chips.has("edited") && !chips.has("unedited")) out.IsEdited = 1;
  if (chips.has("unedited") && !chips.has("edited")) out.IsEdited = 0;
  return out;
}

function deriveHistoryAdvancedCriteria(
  adv: Record<string, string | OptionRenderType[]>
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const dateType = typeof adv.dateType === "string" ? adv.dateType : "";
  if (dateType) out.DateTypeCode = dateType;
  const dateFrom = typeof adv.dateFrom === "string" ? adv.dateFrom : "";
  if (dateFrom) out.DateRangeStart = dateFrom;
  const dateTo = typeof adv.dateTo === "string" ? adv.dateTo : "";
  if (dateTo) out.DateRangeEnd = dateTo;
  const liabilityMin =
    typeof adv.liabilityMin === "string" ? adv.liabilityMin : "";
  if (liabilityMin.trim()) out.MinLiabilityAmount = liabilityMin.trim();
  const liabilityMax =
    typeof adv.liabilityMax === "string" ? adv.liabilityMax : "";
  if (liabilityMax.trim()) out.MaxLiabilityAmount = liabilityMax.trim();
  return out;
}

const HISTORY_PROTECTED_FIELDS: (keyof PolicyManagementRecord)[] = ["uniqueIdentifier"];


const HistoryTab = ({
  records,
  allRecords,
  onUpdateRecords,
  counts,
  stateVals,
  rowsPerPage,
  onRowsPerPageChange,
  page,
  onPageChange,
  totalRows,
  totalPages,
  isLoading,
  sortColumn,
  onSortChange,
  filters: filtersProp,
  onFilterChange,
  onResetFilters,
  onChipCriteriaChange,
  onAdvancedCriteriaChange,
  applyEnabled,
  onApplyFilters,
  onRefresh,
  refetchCounts,
  userTimeZone,
  headerFilterDefaults,
}: HistoryTabProps) => {
  const navigate = useNavigate();

  const handleRefresh = useCallback(() => {
    onRefresh?.();
    refetchCounts?.();
  }, [onRefresh, refetchCounts]);

  const handleRowAction = useCallback(
    (record: PolicyManagementRecord) => {
      navigate(`/policyManagement/product-details/${record.uniqueIdentifier}`, {
        state: { record },
      });
    },
    [navigate]
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Local, not lifted/persisted: a tab switch or chip click should always
  // collapse expanded rows, not preserve them.
  const [expandedRowIds, setExpandedRowIds] = useState<string[]>([]);
  // Column-header filters live in the loader; see filtersProp on props.
  void filtersProp;
  const [activeChips, setActiveChips] = useState<Set<HistoryChip>>(
    new Set<HistoryChip>(["all"])
  );
  const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);
  const [bulkAdjustOpen, setBulkAdjustOpen] = useState(false);
  const [bulkAdjustRecords, setBulkAdjustRecords] = useState<
    PolicyManagementRecord[]
  >([]);
  // Bumped on Reset so the table (and all its uncontrolled column-header
  // inputs) remount with fresh empty state.
  const [resetCounter, setResetCounter] = useState(0);
  const [advancedValues, setAdvancedValues] = useState<
    Record<string, string | OptionRenderType[]>
  >({});

  const [openFileExportModal, setOpenFileExportModal] = useState<boolean>(false);
  const [isExportAll, setIsExportAll] = useState<boolean>(true);


  const handleAdvancedChange = useCallback(
    (key: string, value: string | OptionRenderType[]) => {
      setAdvancedValues((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const filteredRecords = useMemo(() => {
    let result = records;
    // All History chips now drive the server criteria. No client-side chip fallback.

    // Column-header filters are applied server-side via the loader → API.

    // Advanced search: Date range is now server-side via DateTypeCode /
    // DateRangeStart / DateRangeEnd (mirrors Quality Review). No client fallback.

    // Advanced search: Rate/Transaction Code + Policy Type + Record Type +
    // Liability Min/Max are all server-side now (TransCodeList /
    // ProductTypeAbbrTypeCodeList / RecordTypeCodeList / LiabilityAmountMin /
    // LiabilityAmountMax). No client fallback.

    return result;
  }, [records, activeChips]);

  useEffect(() => {
    onChipCriteriaChange?.(deriveHistoryChipCriteria(activeChips));
  }, [activeChips, onChipCriteriaChange]);

  useEffect(() => {
    onAdvancedCriteriaChange?.(deriveHistoryAdvancedCriteria(advancedValues));
  }, [advancedValues, onAdvancedCriteriaChange]);

  const selectedRecords = useMemo(
    () => records.filter((r) => selectedIds.includes(r.uniqueIdentifier)),
    [records, selectedIds]
  );

  const handleBulkAdjustClick = useCallback(() => {
    const notAdjusted = selectedRecords.filter((r) => !r.AdjustmentDate);
    const adjustedCount = selectedRecords.length - notAdjusted.length;

    if (notAdjusted.length === 0) {
      showToast({ type: "info", message: "All selected records are already adjusted." });
      return;
    }

    if (adjustedCount > 0) {
      showToast({
        type: "info",
        message: `${adjustedCount} already-adjusted record${adjustedCount !== 1 ? "s" : ""} skipped from this batch.`,
      });
    }

    setBulkAdjustRecords(notAdjusted);
    setBulkAdjustOpen(true);
  }, [selectedRecords]);

  const handleToggleRow = useCallback((record: PolicyManagementRecord) => {
    setSelectedIds((prev) =>
      prev.includes(record.uniqueIdentifier)
        ? prev.filter((id) => id !== record.uniqueIdentifier)
        : [...prev, record.uniqueIdentifier]
    );
  }, []);

  const handleToggleAllRows = useCallback(() => {
    setSelectedIds((prev) =>
      prev.length === filteredRecords.length
        ? []
        : filteredRecords.map((r) => r.uniqueIdentifier)
    );
  }, [filteredRecords]);

  const handleFiltersChange = useCallback(
    (name: string, value: string | number | null) => {
      onFilterChange?.(name, value);
    },
    [onFilterChange]
  );

  const handleMultiSelectChange = useCallback(
    (
      e: { target: { name: string; value: OptionRenderType[] } },
      value: OptionRenderType[]
    ) => {
      const fieldName = e.target.name;
      const selectedValues = value.map((v) => v.value);
      onFilterChange?.(
        fieldName,
        selectedValues.length > 0 ? selectedValues : null
      );
    },
    [onFilterChange]
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const toggleChip = useCallback((chip: HistoryChip) => {
    setActiveChips(new Set<HistoryChip>([chip]));
    setExpandedRowIds([]);
  }, []);

  const handleResetFilters = useCallback(() => {
    onResetFilters?.();
    setSelectedIds([]);
    setExpandedRowIds([]);
    setActiveChips(new Set<HistoryChip>(["all"]));
    setAdvancedValues({});
    setResetCounter((c) => c + 1);
  }, [onResetFilters]);

  const selectedCount = selectedIds.length;
  // Locked records are excluded from selection; surface the skipped count.
  const lockedNotSelectedCount = useMemo(
    () => filteredRecords.filter((r) => r.isLocked).length,
    [filteredRecords]
  );

  // ---- Column state lifted from HistoryTable ----
  const agentOptions: OptionRenderType[] = useMemo(() => {
    const unique = [...new Set(filteredRecords.map((r) => r.AgentID ?? "").filter(Boolean))];
    return unique.sort().map((v) => ({ label: v, value: v }));
  }, [filteredRecords]);

  // State column filter options: scoped to the page-level State picker when it
  // has a selection, otherwise the full lookup. Mirrors Close Queue.
  const { stateList } = useStateList();
  const stateOptions = useMemo(() => {
    if (stateVals && stateVals.length > 0) {
      return (stateList ?? []).filter((s) => stateVals.includes(s.value));
    }
    return stateList ?? [];
  }, [stateVals, stateList]);

  const productTypeOptions = usePolicyTypeOptions();
  const formPrefixOptions = useFormPrefixOptions();
  const transCodeOptions = useTransCodeOptions();
  const policyFormNameAsync = useAsyncTextFilter(getPolicyHistoryFormNameListByText);
  const fileNumberAsync = useAsyncTextFilter(getPolicyHistoryFileNumberListByText);
  const originalPolicyNumberAsync = useAsyncTextFilter(getPolicyHistoryOriginalPolicyNumberListByText);
  const closedByUserAsync = useAsyncOptionFilter(getPolicyHistoryClosedByListByText);

  const recordTypeOptions = useRecordTypeOptions();

  const propertyTypeOptions = usePropertyTypeOptions();

  const countyOptions = useCountyList(stateVals);

  const dateTypeOptions = useDateTypeOptions("PolicyHistory");

  const advancedSections = useMemo(
    () => [
      {
        title: "Date",
        fields: [
          {
            key: "dateType",
            label: "Date Type",
            type: "select" as const,
            options: dateTypeOptions,
            inline: true,
          },
          {
            key: "dateFrom",
            label: "",
            type: "date" as const,
            maxDate: parseIsoDate(advancedValues.dateTo),
          },
          { key: "dateRangeSeparator", label: "—", type: "separator" as const },
          {
            key: "dateTo",
            label: "",
            type: "date" as const,
            minDate: parseIsoDate(advancedValues.dateFrom),
          },
        ],
      },
      {
        title: "Liability",
        fields: [
          {
            key: "liabilityLabel",
            label: "Liability",
            type: "label" as const,
          },
          {
            key: "liabilityMin",
            label: "",
            type: "number" as const,
            placeholder: "Minimum ($)",
          },
          {
            key: "liabilityRangeSeparator",
            label: "—",
            type: "separator" as const,
          },
          {
            key: "liabilityMax",
            label: "",
            type: "number" as const,
            placeholder: "Maximum ($)",
          },
        ],
      },
    ],
    [dateTypeOptions, advancedValues.dateFrom, advancedValues.dateTo]
  );

  const defaultColumns = useMemo(
    () =>
      buildHistoryColumns({
        agentOptions,
        stateOptions,
        productTypeOptions,
        recordTypeOptions,
        propertyTypeOptions,
        countyOptions,
        formPrefixOptions,
        transCodeOptions,
        policyFormNameAsync,
        fileNumberAsync,
        originalPolicyNumberAsync,
        closedByUserAsync,
        onFiltersChange: handleFiltersChange,
        onMultiSelectChange: handleMultiSelectChange,
        onRowAction: handleRowAction,
        headerFilterDefaults,
      }),
    [
      agentOptions,
      stateOptions,
      productTypeOptions,
      recordTypeOptions,
      propertyTypeOptions,
      countyOptions,
      formPrefixOptions,
      transCodeOptions,
      policyFormNameAsync,
      fileNumberAsync,
      originalPolicyNumberAsync,
      closedByUserAsync,
      handleFiltersChange,
      handleMultiSelectChange,
      handleRowAction,
      headerFilterDefaults,
    ]
  );

  const {
    columns,
    hiddenColumns,
    onColumnResize,
    onColumnsModified,
    setColumnHidden,
    showAllColumns,
    hideAllColumns,
  } = useColumnSettings<PolicyManagementRecord>(
    HISTORY_GRID_NAME,
    defaultColumns,
    HISTORY_PROTECTED_FIELDS
  );

  // The Actions column is non-draggable and must always render last. Stale
  // server-saved column prefs can carry an old position for it (from before the
  // financial columns existed), which mergeOverrides would honor — so pin it to
  // the end here regardless of the saved/default position.
  const orderedColumns = useMemo(() => {
    const actionIdx = columns.findIndex((c) => c.field === "uniqueIdentifier");
    if (actionIdx < 0 || actionIdx === columns.length - 1) return columns;
    const next = [...columns];
    const [actionCol] = next.splice(actionIdx, 1);
    next.push(actionCol);
    return next;
  }, [columns]);

  const chooserOptions = useMemo<MenuColumnItem<PolicyManagementRecord>[]>(
    () =>
      defaultColumns
        .filter((c) => c.field !== "uniqueIdentifier")
        .map((c) => ({
          field: c.field,
          name: c.name,
          checked: !hiddenColumns.includes(c.field),
        })),
    [defaultColumns, hiddenColumns]
  );

  const handleColumnChecked = useCallback(
    (field: keyof PolicyManagementRecord, wasChecked: boolean) => {
      setColumnHidden(field, wasChecked);
    },
    [setColumnHidden]
  );

  const exportedColumns = useMemo(
    () =>
      _orderBy(
        columns.filter((c) => !hiddenColumns.includes(c.field)),
        "position"
      ).map((c) => c.field),
    [columns, hiddenColumns]
  );

  const buildExportPayload = useCallback(
    (exportAll: boolean): PolicyTransactionExportCriteriaDto => {
      const baseCriteria: Partial<PolicyTransactionCriteriaDto> = {
        UserTimeZone: userTimeZone,
        RequestedPage: page,
        StageTypeCode: StageType.HISTORY,
        SortColumn: "receivedDate",
      };

      const searchCriteria = exportAll
        ? {
          ...baseCriteria,
          ListCount: 0,
          PolicyHistoryIDList: null,
        }
        : {
          ...baseCriteria,
          ListCount: selectedIds.length,
          PolicyHistoryIDList: selectedIds.join(", "),
        };

      return {
        ExportColumns: exportedColumns,
        SearchCriteria: searchCriteria,
      };
    },
    [exportedColumns, selectedIds, userTimeZone, page]
  );

  const handleFileExport = async () => {
    if (selectedIds.length > 0) {
      setOpenFileExportModal(true);
    } else {
      const payload = buildExportPayload(isExportAll);
      await exportPolicyHistory(payload);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const chipDefs: { key: HistoryChip; label: string; group: 1 | 2 | 3 | 4 }[] = [
    { key: "all", label: `All (${counts.historyAll})`, group: 1 },
    { key: "reg", label: `REG (${counts.historyReg})`, group: 2 },
    { key: "si", label: `SI (${counts.historySi})`, group: 2 },
    { key: "adj", label: `Adjustment (${counts.historyAdj})`, group: 2 },
    { key: "closed", label: `Closed (${counts.historyClosed})`, group: 3 },
    { key: "sent", label: `Sent (${counts.historySent})`, group: 3 },
    { key: "edited", label: `Edited (${counts.historyEdited})`, group: 4 },
    { key: "unedited", label: `Unedited (${counts.historyUnedited})`, group: 4 },
  ];

  return (
    <TabContainer>
      <ChipRow>
        {/* TODO: re-enable History chips when backend / UX is ready
        {chipDefs.map((c, idx) => {
          const prev = chipDefs[idx - 1];
          const sep = prev && prev.group !== c.group;
          return (
            <React.Fragment key={c.key}>
              {sep && <ChipsSeparator />}
              <Chip
                className={activeChips.has(c.key) ? "active" : ""}
                onClick={() => toggleChip(c.key)}
              >
                {c.label}
              </Chip>
            </React.Fragment>
          );
        })}
        */}
        {selectedCount > 0 && (
          <SelectedCount>
            {selectedCount} record{selectedCount !== 1 ? "s" : ""} selected
            {lockedNotSelectedCount > 0 &&
              `, ${lockedNotSelectedCount} locked, not selected`}
          </SelectedCount>
        )}
        <ToolbarSpacer>
          <ActionButton
            variant="outlined"
            color="secondary"
            onClick={handleResetFilters}
          >
            Reset
          </ActionButton>
          <ActionButton
            variant="outlined"
            color="primary"
            disabled={!applyEnabled}
            onClick={onApplyFilters}
          >
            Apply Filters
          </ActionButton>
          <AdvancedSearchButton
            open={advancedSearchOpen}
            onClick={() => setAdvancedSearchOpen((o) => !o)}
          />
          <Box sx={{ "& .MuiButton-root": { textTransform: "none" } }}>
            <ColumnsConfig
              options={chooserOptions}
              onChecked={handleColumnChecked}
              onShowAll={showAllColumns}
              onHideAll={hideAllColumns}
            />
          </Box>
          <ExportButton onClick={handleFileExport} />
        </ToolbarSpacer>
      </ChipRow>

      <AdvancedSearchPanel
        open={advancedSearchOpen}
        sections={advancedSections}
        values={advancedValues}
        onChange={handleAdvancedChange}
        onClear={() => setAdvancedValues({})}
      />

      <BulkActionBar open={selectedCount > 0} count={selectedCount}>
        <BulkButton
          icon={<PublishedWithChangesIcon />}
          label="Bulk Adjust"
          onClick={handleBulkAdjustClick}
          primary
        />
      </BulkActionBar>

      <HistoryTable
        key={resetCounter}
        records={filteredRecords}
        selectedIds={selectedIds}
        onToggleRow={handleToggleRow}
        onToggleAllRows={handleToggleAllRows}
        columns={orderedColumns}
        hiddenColumns={hiddenColumns}
        onColumnResize={onColumnResize}
        onColumnsModified={onColumnsModified}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        page={page}
        onPageChange={onPageChange}
        totalRows={totalRows}
        totalPages={totalPages}
        isLoading={isLoading}
        sortColumn={sortColumn}
        onSortChange={onSortChange}
        expandedRowIds={expandedRowIds}
        onExpandedRowIdsChange={setExpandedRowIds}
        onRefresh={handleRefresh}
        summaryComponent={
          <PolicyStatsBar records={filteredRecords} variant="history" />
        }
      />
      <FileExportDialog
        open={openFileExportModal}
        onExport={async (exportAll) => {
          setIsExportAll(exportAll);
          setOpenFileExportModal(false);
          const payload = buildExportPayload(exportAll);
          await exportPolicyHistory(payload);
        }}
        onCancel={() => setOpenFileExportModal(false)}
        showSelectionPrompt={selectedIds?.some((s) => s)}
        selectionPrompt={"Include only the selected policies or all policies?"}
        icon={IosShareIcon}
      />

      <BulkAdjustModal
        open={bulkAdjustOpen}
        records={bulkAdjustRecords}
        onClose={() => setBulkAdjustOpen(false)}
        onRefresh={handleRefresh}
      />
    </TabContainer>
  );
};

export default HistoryTab;
